import { UserPreferenceType } from '../types/UserPreferences'
import { getAuth, JwtPayload } from '../app/modules/auth'
import jwtDecode from 'jwt-decode'
import { Role, UserInvestor } from '../app/pages/store/userRoleGrants'
import { UserProject } from '../generated/graphql'

// Keys that must survive an account switch. The account list is user-global and
// keeps the switcher stable while the new account context is loading. Everything
// account-scoped is rebuilt on reload to avoid showing data from the previous account.
const ACCOUNT_SWITCH_PRESERVE_KEYS = ['accessToken', 'userId', 'userPreferences', 'accounts']

export function clearAccountScopedSession(nextActiveAccount?: { id: string; name: string }) {
  const preserved: Record<string, string> = {}
  ACCOUNT_SWITCH_PRESERVE_KEYS.forEach((key) => {
    const value = sessionStorage.getItem(key)
    if (value !== null) preserved[key] = value
  })
  sessionStorage.clear()
  Object.entries(preserved).forEach(([key, value]) => sessionStorage.setItem(key, value))
  // Optimistically seed the switched-to account so the UI shows it immediately
  // on reload, before the boot-time user-context fetch resolves — no flash of
  // the previous account. saveRole overwrites it with the same server value.
  if (nextActiveAccount) {
    setActiveAccount(nextActiveAccount)
  }
}

export function setUserId(id: number) {
  sessionStorage.setItem('userId', id.toString())
}

export function getUserId() {
  let userId = undefined
  const auth = getAuth()
  if (auth) {
    const jwt = jwtDecode<JwtPayload>(auth)
    userId = Number(jwt.id)
  }
  return userId
}

export function setUserPreferences(object: UserPreferenceType) {
  sessionStorage.setItem('userPreferences', JSON.stringify(object))
}

export function setUserRoles(key: string, value: Role[]) {
  sessionStorage.setItem(key, JSON.stringify(value))
}

export function setUserProjects(key: string, value: UserProject[]) {
  sessionStorage.setItem(key, JSON.stringify(value))
}

export function setUserInvestors(key: string, value: UserInvestor[]) {
  sessionStorage.setItem(key, JSON.stringify(value))
}

export type UserAccount = {
  id: string
  name: string
  isDefault: boolean
}

export function setAccounts(value: UserAccount[]) {
  sessionStorage.setItem('accounts', JSON.stringify(value || []))
}

export function getAccounts(): UserAccount[] {
  const data = sessionStorage.getItem('accounts')
  return data ? JSON.parse(data) : []
}

export function setActiveAccount(value: { id: string; name: string } | null) {
  if (value) {
    sessionStorage.setItem('activeAccount', JSON.stringify(value))
  } else {
    sessionStorage.removeItem('activeAccount')
  }
}

export function getActiveAccount(): { id: string; name: string } | null {
  const data = sessionStorage.getItem('activeAccount')
  return data ? JSON.parse(data) : null
}

export function getUserInvestors(key: string) {
  const data = sessionStorage.getItem(key)
  if (data) {
    const roles: UserInvestor[] = JSON.parse(data)
    return roles
  } else {
    return []
  }
}

export function getUserProjects(key: string): UserProject[] {
  const data = sessionStorage.getItem(key)
  if (data) {
    const roles: UserProject[] = JSON.parse(data)
    return roles
  } else {
    return []
  }
}

export function getUserRoles(key: string) {
  const data = sessionStorage.getItem(key)
  if (data) {
    const roles: Role[] = JSON.parse(data)
    return roles
  } else {
    return []
  }
}

export function setStringItem(key: string, value: string) {
  sessionStorage.setItem(key, value)
}

export function getStringItem(key: string) {
  return sessionStorage.getItem(key)
}

export function removeItem(key: string) {
  sessionStorage.removeItem(key)
}

export function getUserPreferences(id: number) {
  let userPref = sessionStorage.getItem('userPreferences')
  if (userPref) {
    let object: UserPreferenceType = JSON.parse(userPref)
    if (Number(object?.id) === id) {
      return object
    } else return undefined
  }
  return undefined
}

export function setDashboardMetricEnabled(name: string) {
  let userId = getUserId()
  let userPreferencesObj = userId ? getUserPreferences(userId) : null

  if (userPreferencesObj) {
    let dashboardMetrics = userPreferencesObj.dashboardMetrics
    if (!dashboardMetrics.includes(name)) {
      dashboardMetrics.push(name)
    } else {
      let index = dashboardMetrics.indexOf(name)
      if (index > -1) dashboardMetrics.splice(index, 1)
    }
    const newUserPref = { ...userPreferencesObj, dashboardMetrics: dashboardMetrics }
    setUserPreferences(newUserPref)
  }
}

export function isDashboardMetricEnabled(name: string) {
  let userId = getUserId()
  let userPreferencesObj = userId ? getUserPreferences(userId) : null

  if (userPreferencesObj) {
    if (userPreferencesObj.dashboardMetrics.includes(name)) {
      return true
    } else {
      return false
    }
  }
  return false
}
export function setPortfolioMetricEnabled(name: string) {
  let userId = getUserId()
  let userPreferencesObj = userId ? getUserPreferences(userId) : null

  if (userPreferencesObj) {
    let portfolioMetrics = userPreferencesObj.portfolioMetrics
    if (!portfolioMetrics.includes(name)) {
      portfolioMetrics.push(name)
    } else {
      let index = portfolioMetrics.indexOf(name)
      if (index > -1) portfolioMetrics.splice(index, 1)
    }
    const newUserPref = { ...userPreferencesObj, portfolioMetrics: portfolioMetrics }
    setUserPreferences(newUserPref)
  }
}

export function isPortfolioMetricEnabled(name: string) {
  let userId = getUserId()
  let userPreferencesObj = userId ? getUserPreferences(userId) : null

  if (userPreferencesObj) {
    if (userPreferencesObj.portfolioMetrics.includes(name)) {
      return true
    } else {
      return false
    }
  }
  return false
}
